const DEFAULT_MAX_BOT_USERNAME = "caremybot";
const DEFAULT_TG_BOT_LINK = "https://t.me/caremybot";
const DEFAULT_MAX_SUPPORT_BOT_URL = "https://max.ru/caremybot";
const DEFAULT_TG_SUPPORT_BOT = "chat_cmbot";
const PLATFORM_STORAGE_KEY = "lk_platform";

const runtimeConfig = {
  maxBotUsername: DEFAULT_MAX_BOT_USERNAME,
  maxSupportBotUrl: DEFAULT_MAX_SUPPORT_BOT_URL,
};

let runtimeLoaded = false;

const toMaxUrl = (value, fallbackUsername = DEFAULT_MAX_BOT_USERNAME) => {
  if (!value) return `https://max.ru/${fallbackUsername}`;
  if (/^https?:\/\//i.test(value)) return value;
  return `https://max.ru/${value.replace(/^@/, "")}`;
};

const readStoredPlatform = () => {
  try {
    return sessionStorage.getItem(PLATFORM_STORAGE_KEY);
  } catch (_) {
    return null;
  }
};

const writeStoredPlatform = (platform) => {
  if (platform !== "max" && platform !== "telegram") return;
  try {
    sessionStorage.setItem(PLATFORM_STORAGE_KEY, platform);
  } catch (_) {
    // ignore quota / private mode
  }
};

export const loadRuntimeConfig = async () => {
  if (runtimeLoaded) return runtimeConfig;
  runtimeLoaded = true;
  try {
    const res = await fetch("/runtime");
    if (!res.ok) return runtimeConfig;
    const data = await res.json();
    if (data?.maxBotUsername) {
      runtimeConfig.maxBotUsername = data.maxBotUsername;
    }
    if (data?.maxSupportBotUrl) {
      runtimeConfig.maxSupportBotUrl = toMaxUrl(
        data.maxSupportBotUrl,
        data.maxBotUsername || runtimeConfig.maxBotUsername,
      );
    }
  } catch (_) {
    // defaults
  }
  return runtimeConfig;
};

const hasMaxHash = () => {
  const hash = window.location.hash || "";
  return /(?:^|[&#])WebAppData=/.test(hash) || /WebAppPlatform=/.test(hash);
};

const hasTelegramHash = () => {
  const hash = window.location.hash || "";
  return /tgWebAppData/.test(hash) || /tgWebAppVersion/.test(hash);
};

const hasMaxWebAppUser = () =>
  !!(window.WebApp?.initDataUnsafe?.user?.id || window.WebApp?.initData);

const hasTelegramWebAppUser = () =>
  !!(
    window.Telegram?.WebApp?.initDataUnsafe?.user?.id ||
    window.Telegram?.WebApp?.initData
  );

export const getPlatform = () => {
  // Telegram first: leftover sessionStorage "max" must not steal a real TG session.
  if (hasTelegramHash()) {
    writeStoredPlatform("telegram");
    return "telegram";
  }
  if (hasTelegramWebAppUser()) {
    writeStoredPlatform("telegram");
    return "telegram";
  }
  if (hasMaxHash()) {
    writeStoredPlatform("max");
    return "max";
  }
  if (hasMaxWebAppUser()) {
    writeStoredPlatform("max");
    return "max";
  }

  const stored = readStoredPlatform();
  if (stored === "max" || stored === "telegram") {
    return stored;
  }

  // Telegram SDK stub can exist without a real session — do not treat it as TG.
  return "unknown";
};

export const getWebApp = () => {
  const platform = getPlatform();
  if (platform === "telegram") {
    return window.Telegram?.WebApp || null;
  }
  if (platform === "max") {
    return window.WebApp || null;
  }
  if (
    window.Telegram?.WebApp?.initDataUnsafe?.user?.id ||
    window.Telegram?.WebApp?.initData
  ) {
    return window.Telegram.WebApp;
  }
  if (window.WebApp?.initDataUnsafe?.user?.id || window.WebApp?.initData) {
    return window.WebApp;
  }
  return window.Telegram?.WebApp || window.WebApp || null;
};

/** @deprecated use getWebApp */
export const getTelegramWebApp = () => getWebApp();

export const getChatId = () => {
  const webApp = getWebApp();
  return webApp?.initDataUnsafe?.user?.id ?? null;
};

export const getInitData = () => {
  const webApp = getWebApp();
  return webApp?.initData ?? "";
};

export const getBotLink = () => {
  if (getPlatform() === "max") {
    const username = runtimeConfig.maxBotUsername || DEFAULT_MAX_BOT_USERNAME;
    return `https://max.ru/${username}`;
  }
  return DEFAULT_TG_BOT_LINK;
};

export const getSupportBotLink = (startPayload) => {
  if (getPlatform() === "max") {
    return runtimeConfig.maxSupportBotUrl || DEFAULT_MAX_SUPPORT_BOT_URL;
  }
  if (startPayload) {
    return `https://t.me/${DEFAULT_TG_SUPPORT_BOT}?start=${startPayload}`;
  }
  return `https://t.me/${DEFAULT_TG_SUPPORT_BOT}?start=lk_tg`;
};

export const getMessengerName = () =>
  getPlatform() === "max" ? "MAX" : "Telegram";

export const showBackButton = () => {
  const webApp = getWebApp();
  webApp?.BackButton?.show?.();
};

export const hideBackButton = () => {
  const webApp = getWebApp();
  webApp?.BackButton?.hide?.();
};

export const onBackButton = (handler) => {
  const webApp = getWebApp();
  if (!webApp?.BackButton) return () => {};

  if (typeof webApp.BackButton.onClick === "function") {
    webApp.BackButton.onClick(handler);
    return () => {
      if (typeof webApp.BackButton.offClick === "function") {
        webApp.BackButton.offClick(handler);
      }
    };
  }

  if (typeof webApp.onEvent === "function") {
    webApp.onEvent("backButtonClicked", handler);
    return () => {
      if (typeof webApp.offEvent === "function") {
        webApp.offEvent("backButtonClicked", handler);
      }
    };
  }

  return () => {};
};

export const requestFullscreenSafe = () => {
  if (getPlatform() !== "telegram") return;
  const webApp = getWebApp();
  if (typeof webApp?.requestFullscreen === "function") {
    webApp.requestFullscreen();
  }
};

export const onFullscreenChanged = (handler) => {
  if (getPlatform() !== "telegram") return () => {};
  const webApp = getWebApp();
  if (typeof webApp?.onEvent !== "function") return () => {};
  webApp.onEvent("fullscreenChanged", handler);
  return () => {
    if (typeof webApp.offEvent === "function") {
      webApp.offEvent("fullscreenChanged", handler);
    }
  };
};

export const ensurePlatformAccess = () => {
  const chatId = getChatId();
  if (chatId == null) {
    window.location.href = "/accessError/";
    return false;
  }
  return true;
};

/**
 * Opens an https link from Mini App (Telegram / MAX).
 * Prefer native openLink when a real WebApp session exists; otherwise fall back
 * to window.open / location — target=_blank alone is blocked inside WebViews.
 */
export const openExternalLink = (url) => {
  if (!url) return;

  const wa = getWebApp();
  const hasSession = !!(wa?.initData || wa?.initDataUnsafe?.user?.id);

  if (hasSession && typeof wa.openLink === "function") {
    try {
      wa.openLink(url);
      return;
    } catch (err) {
      console.error("openLink failed", err);
    }
  }

  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    window.location.assign(url);
  }
};
